/**
 * Campaign Slider JavaScript
 * Version: 2.1 (Deferred slide hydration)
 *
 * 2.1: Slides 3+ ship with data-src/data-srcset (see section liquid v2.4).
 *      Visible slides are hydrated on init; the rest after window load
 *      during idle time, or immediately on slider interaction.
 */

(function() {
  'use strict';

  class CampaignSlider {
    constructor(container) {
      this.container = container;
      this.viewport = container.querySelector('.campaign-slider-viewport');
      this.track = container.querySelector('.campaign-slider');
      this.slides = container.querySelectorAll('.campaign-slide');
      this.prevBtn = container.querySelector('.campaign-slider__nav--prev');
      this.nextBtn = container.querySelector('.campaign-slider__nav--next');

      this.slidesDesktop = parseFloat(this.container.dataset.slidesDesktop) || 2.5;
      this.slidesMobile = 1;
      this.totalSlides = parseInt(this.track.dataset.totalSlides, 10) || this.slides.length;
      this.currentPage = 0;
      this.isTransitioning = false;
      this.rafId = null;
      this.resizeObserver = null;
      this.fullyHydrated = false;

      this.init();
    }

    init() {
      this.hydrateInitialSlides();
      this.updateSlideWidths();
      this.setupEventListeners();
      this.updateNavVisibility();
      this.scheduleFullHydration();
    }

    // ========================================================================
    // Deferred slide hydration (v2.1)
    // Slides 3+ ship with data-src/data-srcset so their images do not compete
    // with the LCP image during initial page load.
    // ========================================================================

    hydrateSlide(slide) {
      slide.querySelectorAll('source[data-srcset], img[data-srcset], img[data-src]').forEach(el => {
        if (el.dataset.srcset) {
          el.setAttribute('srcset', el.dataset.srcset);
          el.removeAttribute('data-srcset');
        }
        if (el.dataset.src) {
          el.setAttribute('src', el.dataset.src);
          el.removeAttribute('data-src');
        }
      });
    }

    hydrateInitialSlides() {
      const isMobile = window.innerWidth < 768;
      const visible = isMobile ? 2 : Math.ceil(this.slidesDesktop) + 1;
      const count = Math.min(visible, this.slides.length);
      for (let i = 0; i < count; i++) {
        this.hydrateSlide(this.slides[i]);
      }
    }

    hydrateAllSlides() {
      if (this.fullyHydrated) return;
      this.fullyHydrated = true;
      this.slides.forEach(slide => this.hydrateSlide(slide));
    }

    scheduleFullHydration() {
      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        this.hydrateAllSlides();
        return;
      }
      const run = () => {
        if (window.requestIdleCallback) {
          requestIdleCallback(() => this.hydrateAllSlides(), { timeout: 2000 });
        } else {
          setTimeout(() => this.hydrateAllSlides(), 500);
        }
      };
      if (document.readyState === 'complete') {
        run();
      } else {
        window.addEventListener('load', run, { once: true });
      }
    }

    updateSlideWidths() {
      const isMobile = window.innerWidth < 768;
      const visibleSlides = isMobile ? this.slidesMobile : this.slidesDesktop;
      const gapProperty = isMobile ? '--slide-gap-mobile' : '--slide-gap';
      const gapPx = parseInt(getComputedStyle(this.container).getPropertyValue(gapProperty), 10) || (isMobile ? 12 : 16);

      if (!isMobile) {
        const containerWidth = this.viewport.offsetWidth;
        const totalGaps = gapPx * (visibleSlides - 1);
        const availableWidth = containerWidth - totalGaps;
        const slideWidth = availableWidth / visibleSlides;

        this.slides.forEach(slide => {
          slide.style.width = `${slideWidth}px`;
        });

        this.slideWidth = slideWidth;
        this.gap = gapPx;
        this.slidesPerView = visibleSlides;
        this.totalPages = Math.ceil(this.totalSlides / visibleSlides);
        this.viewportWidth = slideWidth * visibleSlides + gapPx * (visibleSlides - 1);
      } else {
        const styles = getComputedStyle(document.documentElement);
        const pageGutter = parseInt(styles.getPropertyValue('--cs-page-gutter'), 10) || 16;
        const peek = parseInt(styles.getPropertyValue('--cs-peek'), 10) || 48;
        const slideWidth = window.innerWidth - (pageGutter * 2) - peek;

        this.slides.forEach(slide => {
          slide.style.width = `${slideWidth}px`;
        });

        this.slideWidth = slideWidth;
        this.gap = gapPx;
        this.slidesPerView = 1;
        this.totalPages = Math.ceil(this.totalSlides / this.slidesPerView);
        this.viewportWidth = slideWidth + this.gap;
      }
    }

    setupEventListeners() {
      // Navigation buttons
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.prev());
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.next());
      }

      // Resize handling
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.debounce(() => {
          this.updateSlideWidths();
          this.currentPage = 0;
          this.viewport.scrollLeft = 0;
          this.updateNavVisibility();
        }, 100));
        this.resizeObserver.observe(this.viewport);
      } else {
        window.addEventListener('resize', this.debounce(() => {
          this.updateSlideWidths();
          this.currentPage = 0;
          this.viewport.scrollLeft = 0;
          this.updateNavVisibility();
        }, 250));
      }

      // Scroll handling with RAF
      this.viewport.addEventListener('scroll', () => {
        this.hydrateAllSlides();
        if (this.rafId) return;
        this.rafId = requestAnimationFrame(() => {
          this.handleScrollUpdate();
          this.rafId = null;
        });
      }, { passive: true });
    }

    debounce(fn, delay) {
      let timeoutId;
      return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn.apply(this, args), delay);
      };
    }

    handleScrollUpdate() {
      const scrollLeft = this.viewport.scrollLeft;
      const scrollWidth = this.viewport.scrollWidth - this.viewport.offsetWidth;

      // At end
      if (scrollLeft >= scrollWidth - 10) {
        const lastPage = this.totalPages - 1;
        if (this.currentPage !== lastPage) {
          this.currentPage = lastPage;
          this.updateNavVisibility();
        }
        return;
      }

      // At start
      if (scrollLeft <= 10) {
        if (this.currentPage !== 0) {
          this.currentPage = 0;
          this.updateNavVisibility();
        }
        return;
      }

      // Middle
      const currentPage = Math.round(scrollLeft / this.viewportWidth);
      const clampedPage = Math.max(0, Math.min(currentPage, this.totalPages - 1));

      if (clampedPage !== this.currentPage) {
        this.currentPage = clampedPage;
        this.updateNavVisibility();
      }
    }

    prev() {
      if (this.isTransitioning) return;
      this.goToPage(Math.max(0, this.currentPage - 1));
    }

    next() {
      if (this.isTransitioning) return;
      this.goToPage(Math.min(this.totalPages - 1, this.currentPage + 1));
    }

    goToPage(pageIndex) {
      if (pageIndex < 0 || pageIndex >= this.totalPages) return;

      this.hydrateAllSlides();
      this.isTransitioning = true;
      this.currentPage = pageIndex;

      const scrollPosition = pageIndex * this.viewportWidth;
      this.viewport.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });

      this.updateNavVisibility();

      setTimeout(() => {
        this.isTransitioning = false;
      }, 300);
    }

    updateNavVisibility() {
      if (!this.prevBtn || !this.nextBtn) return;

      const atStart = this.currentPage <= 0;
      const atEnd = this.currentPage >= this.totalPages - 1;

      this.prevBtn.classList.toggle('hidden', atStart);
      this.nextBtn.classList.toggle('hidden', atEnd);
    }

    destroy() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
      }
    }
  }

  // ==========================================================================
  // Initialization
  // ==========================================================================
  
  function initSliders() {
    const sliders = document.querySelectorAll('.campaign-slider-wrapper');
    sliders.forEach(slider => {
      if (!slider.sliderInstance) {
        slider.sliderInstance = new CampaignSlider(slider);
      }
    });
  }

  // Init on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSliders);
  } else {
    initSliders();
  }

  // Shopify Theme Editor support
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', event => {
      const slider = event.target.querySelector('.campaign-slider-wrapper');
      if (slider) {
        if (slider.sliderInstance) {
          slider.sliderInstance.destroy();
        }
        slider.sliderInstance = new CampaignSlider(slider);
      }
    });

    document.addEventListener('shopify:section:unload', event => {
      const slider = event.target.querySelector('.campaign-slider-wrapper');
      if (slider?.sliderInstance) {
        slider.sliderInstance.destroy();
        slider.sliderInstance = null;
      }
    });
  }

  // Export for external use
  window.CampaignSlider = CampaignSlider;

})();